class CollectionListInfinite extends HTMLElement {
  #observer = null;
  #sentinel = null;
  #grid = null;
  #templateCard = null;
  #templateData = null;
  #remainingHandles = [];
  #fetchedByHandle = new Map();
  #batchSize = 12;
  #loading = false;
  #shopDomain = '';
  #token = '';

  connectedCallback() {
    this.#shopDomain = (this.dataset.shopDomain || '').trim();
    this.#token = (this.dataset.storefrontToken || '').trim();
    this.#batchSize = Math.max(1, parseInt(this.dataset.batchSize || '12', 10) || 12);
    this.#sentinel = this.querySelector('[data-cl-sentinel]');
    this.#grid = this.querySelector('.resource-list--grid');
    this.#templateCard = this.querySelector('[data-cl-template-card]');

    const tplDataScript = this.querySelector('script[data-cl-template-data]');
    const handlesScript = this.querySelector('script[data-cl-remaining-handles]');
    try {
      this.#templateData = tplDataScript ? JSON.parse(tplDataScript.textContent.trim()) : null;
      this.#remainingHandles = handlesScript ? JSON.parse(handlesScript.textContent.trim()) : [];
    } catch (err) {
      console.warn('[cl-infinite] failed to parse data scripts', err);
      return;
    }

    if (!this.#shopDomain || !this.#token || !this.#sentinel || !this.#grid || !this.#templateCard || !this.#templateData) return;
    if (typeof IntersectionObserver !== 'function') return;

    this.#tryExpandFromMetaobject().finally(() => {
      if (!Array.isArray(this.#remainingHandles) || this.#remainingHandles.length === 0) {
        this.#sentinel?.remove();
        return;
      }
      this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '1500px 0px', threshold: 0 });
      this.#observer.observe(this.#sentinel);
    });
  }

  async #tryExpandFromMetaobject() {
    const type = (this.dataset.clMetaobjectType || '').trim();
    const field = (this.dataset.clMetaobjectField || '').trim();
    if (!type || !field) return;
    const tplHandle = this.#templateData?.handle;
    if (!tplHandle) return;

    const gql = `
      query CollectionListMetaobjects($type: String!, $field: String!) {
        metaobjects(type: $type, first: 50) {
          nodes {
            handle
            field(key: $field) {
              references(first: 250) {
                nodes { ... on Collection { handle } }
              }
            }
          }
        }
      }
    `;
    try {
      const res = await fetch(`https://${this.#shopDomain}/api/2026-01/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.#token },
        body: JSON.stringify({ query: gql, variables: { type, field } }),
      });
      if (!res.ok) throw new Error(`Storefront API ${res.status}`);
      const json = await res.json();
      const entries = json?.data?.metaobjects?.nodes;
      if (!Array.isArray(entries)) return;

      let match = null;
      for (const entry of entries) {
        const refs = entry?.field?.references?.nodes;
        if (!Array.isArray(refs)) continue;
        if (refs.some((c) => c?.handle === tplHandle)) {
          match = refs.map((c) => c?.handle).filter(Boolean);
          break;
        }
      }
      if (!match) return;

      const renderedCount = this.#grid?.querySelectorAll(':scope > .resource-list__item').length || 0;
      this.#remainingHandles = match.slice(renderedCount);
    } catch (err) {
      console.warn('[cl-infinite] metaobject expand failed', err);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #onIntersect = (entries) => {
    if (this.#loading) return;
    if (!entries.some((e) => e.isIntersecting)) return;
    this.#loadNextBatch();
  };

  async #loadNextBatch() {
    if (this.#remainingHandles.length === 0) {
      this.#stop();
      return;
    }

    this.#loading = true;
    this.setAttribute('aria-busy', 'true');

    const batch = this.#remainingHandles.splice(0, this.#batchSize);

    try {
      await this.#ensureFetched(batch);
      const frag = document.createDocumentFragment();
      for (const handle of batch) {
        const data = this.#fetchedByHandle.get(handle);
        if (!data) continue;
        const card = this.#buildCard(data);
        if (card) frag.appendChild(card);
      }
      this.#grid.appendChild(frag);

      if (this.#remainingHandles.length === 0) this.#stop();
    } catch (err) {
      console.warn('[cl-infinite] batch failed', err);
      this.#remainingHandles.unshift(...batch);
      this.#stop();
    } finally {
      this.#loading = false;
      this.removeAttribute('aria-busy');
    }

    if (this.#remainingHandles.length > 0 && this.#sentinelStillNear()) {
      this.#loadNextBatch();
    }
  }

  #sentinelStillNear() {
    if (!this.#sentinel) return false;
    const rect = this.#sentinel.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.top < vh + 1500;
  }

  #stop() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#sentinel?.remove();
  }

  async #ensureFetched(handles) {
    const missing = handles.filter((h) => !this.#fetchedByHandle.has(h));
    if (missing.length === 0) return;

    const aliases = missing
      .map((h, i) => `c${i}: collection(handle: "${this.#escapeQueryValue(h)}") {
        handle
        title
        onlineStoreUrl
        image { url(transform: { maxWidth: 800 }) altText }
      }`)
      .join('\n');
    const gql = `query CollectionsByHandle { ${aliases} }`;

    const res = await fetch(`https://${this.#shopDomain}/api/2026-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.#token,
      },
      body: JSON.stringify({ query: gql }),
    });
    if (!res.ok) throw new Error(`Storefront API ${res.status}`);
    const json = await res.json();
    const data = json?.data || {};
    for (let i = 0; i < missing.length; i++) {
      const n = data[`c${i}`];
      const h = missing[i];
      if (!n) continue;
      this.#fetchedByHandle.set(h, {
        handle: n.handle || h,
        title: n.title || '',
        url: n.onlineStoreUrl || `/collections/${n.handle || h}`,
        image: n.image?.url || '',
        imageAlt: n.image?.altText || n.title || '',
      });
    }
  }

  #escapeQueryValue(v) {
    return String(v).replace(/[\\"]/g, (c) => `\\${c}`);
  }

  #buildCard(data) {
    const clone = this.#templateCard.cloneNode(true);
    clone.removeAttribute('data-cl-template-card');

    const tpl = this.#templateData;
    const tplUrl = tpl.url || '';
    const tplTitle = tpl.title || '';
    const tplHandle = tpl.handle || '';

    for (const a of clone.querySelectorAll('a[href]')) {
      const href = a.getAttribute('href') || '';
      if (href === tplUrl || href.endsWith(`/collections/${tplHandle}`)) {
        a.setAttribute('href', data.url);
      }
    }

    for (const img of clone.querySelectorAll('img')) {
      if (data.image) {
        img.setAttribute('src', data.image);
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('width');
        img.removeAttribute('height');
        img.setAttribute('loading', 'lazy');
      }
      if (data.imageAlt) img.setAttribute('alt', data.imageAlt);
    }

    const norm = (s) => String(s || '').normalize('NFC').replace(/\s+/g, '').toLowerCase();
    const tplTitleNorm = norm(tplTitle);

    for (const el of clone.querySelectorAll('.visually-hidden')) {
      if (norm(el.textContent) === tplTitleNorm) {
        el.textContent = data.title;
      }
    }
    for (const el of clone.querySelectorAll('p')) {
      if (norm(el.textContent) !== tplTitleNorm) continue;
      const words = (data.title || '').trim().split(/\s+/);
      if (words.length > 1) {
        const first = this.#escapeHtml(words[0]);
        const rest = this.#escapeHtml(words.slice(1).join(' '));
        el.innerHTML = `${first}<br>${rest}`;
      } else {
        el.textContent = data.title;
      }
    }

    return clone;
  }

  #escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('collection-list-infinite')) {
  customElements.define('collection-list-infinite', CollectionListInfinite);
}
